/**
 * Environment variables utility
 * Supports:
 * 1. Local .env files (Vite's built-in system)
 * 2. Local JSON config file (public/config.json) for testing S3 flow
 * 3. S3 JSON config file (production) via VITE_CONFIG_URL
 */

interface EnvConfig {
  VITE_API_BASE_URL: string;
  VITE_APP_NAME: string;
  VITE_ENVIRONMENT: string;
  VITE_CLIENT_ID?: string;
  VITE_TENANT_ID?: string;
  VITE_REDIRECT_URI?: string;
  VITE_POST_LOGOUT_REDIRECT_URI?: string;
  USE_DUMMY_DATA?: boolean;
  VITE_AWS_ACCESS_PORTAL_URL?: string;
  VITE_AMAZON_CONNECT_CCP_URL?: string;
  VITE_AMAZON_CONNECT_REGION?: string;
  [key: string]: string | boolean | undefined;
}

let envConfig: EnvConfig | null = null;

/**
 * Loads environment configuration from:
 * 1. S3 (production) if VITE_CONFIG_URL is set
 * 2. Local JSON file (public/config.json) if in development and file exists
 * 3. Falls back to .env files (Vite's built-in system)
 *
 * This should be called before the app renders
 */
export async function loadEnvConfig(): Promise<void> {
  // Priority 1: Load from S3 if VITE_CONFIG_URL is set (works in both dev and prod)
  if (import.meta.env.VITE_CONFIG_URL) {
    try {
      const response = await fetch(import.meta.env.VITE_CONFIG_URL);
      if (response.ok) {
        envConfig = await response.json();
        console.log('✅ Loaded config from S3:', envConfig);

        // Merge with existing env vars (S3 config takes precedence)
        Object.assign(import.meta.env, envConfig);
      }
    } catch (error) {
      console.warn(
        '⚠️ Failed to load config from S3, using local env vars',
        error
      );
    }
  }
  // Priority 2: Development - Try loading from local JSON file (for testing S3 flow)
  if (import.meta.env.DEV && !envConfig) {
    // Check if we should use local config file (for testing)
    const useLocalConfig = import.meta.env.VITE_USE_LOCAL_CONFIG === 'true';

    if (useLocalConfig) {
      try {
        // Load from public/config.json (served by Vite dev server)
        const response = await fetch('/config.json');
        if (response.ok) {
          envConfig = await response.json();
          console.log('✅ Loaded config from local JSON file:', envConfig);
          // Merge with existing env vars (JSON config takes precedence)
          Object.assign(import.meta.env, envConfig);
        }
      } catch (error) {
        console.warn(
          '⚠️ Failed to load local config.json, using .env files',
          error
        );
      }
    }
  }

  // Priority 3: Set defaults from .env files or fallback values
  envConfig = {
    VITE_API_BASE_URL:
      import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api',
    VITE_APP_NAME: import.meta.env.VITE_APP_NAME || 'ESP Application',
    VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT || 'development',
    USE_DUMMY_DATA: import.meta.env.USE_DUMMY_DATA === 'true',
    ...envConfig, // Config from JSON/S3 takes precedence
  };

  console.log('🔧 Final envConfig:', envConfig);
}

/**
 * Get environment variable value
 */
export function getEnv(key: string, defaultValue = ''): string {
  const value = envConfig?.[key] ?? import.meta.env[key];
  if (typeof value === 'boolean') return String(value);
  return value ?? defaultValue;
}
